import { useState } from 'react';
import { Alert, Empty, Flex } from 'antd';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { BackLink } from '@/components/back-link/back-link';
import { Button } from '@/components/button/button';
import Callout from '@/components/callout/callout';
import { ExportButton } from '@/components/export-button/export-button';
import FactTile from '@/components/fact-tile/fact-tile';
import FilterChip from '@/components/filter-chip/filter-chip';
import KpiTile from '@/components/kpi-tile/kpi-tile';
import Loader from '@/components/loader/loader';
import LoadMoreRows from '@/components/load-more-rows/load-more-rows';
import SearchField from '@/components/search-field/search-field';
import SectionLabel from '@/components/section-label/section-label';
import StatusBadge from '@/components/status-badge/status-badge';
import { useDebouncedFilterQuery } from '@/hooks/use-debounced-filter-query/use-debounced-filter-query';
import {
  useCampaignAction,
  useCampaignEntries,
  useOutboundCampaign,
} from '@/hooks/use-outbound-campaigns/use-outbound-campaigns';
import { useOutboundScenarios } from '@/hooks/use-outbound-scenarios/use-outbound-scenarios';
import { useScreenSize } from '@/hooks/use-screen-size/use-screen-size';
import { formatNumber } from '@/i18n/format';
import { exportCampaignEntries } from '@/services/outbound-calls-service/campaigns.service';
import {
  CampaignAction,
  CampaignActionResult,
  OutboundCampaign,
} from '@/services/outbound-calls-service/campaigns.service.types';
import {
  CampaignStatus,
  ENTRY_STATUS_FILTERS,
  EntryStatusFilter,
  OUTBOUND_MAX_ROWS,
  OUTBOUND_PAGE_SIZE,
  OUTBOUND_POLL_INTERVAL_MS,
} from '@/services/outbound-calls-service/outbound-calls.constants';
import { downloadFile } from '@/utils/helpers';
import { languageName } from '@/utils/language-name';
import { openNotification } from '@/utils/notification/notification';
import { useOutboundContext } from '../outbound-calls.context';
import {
  campaignActivityLabel,
  campaignRefusalLabel,
  isNotFound,
  outboundErrorMessage,
} from '../outbound-calls.messages';
import {
  CAMPAIGN_STATUS_BADGE,
  countOf,
  formatCallDays,
  toHourMinute,
} from '../outbound-calls.utils';
import { CampaignEntriesTable } from './campaign-entries-table';
import { CampaignFormModal } from './campaign-form-modal';
import { CampaignImportModal } from './campaign-import-modal';
import { LeadDrawer } from './lead-drawer';
import styles from '../outbound-calls.module.scss';

const CAMPAIGNS_PATH = '/outbound-calls/campaigns';

// Retrying is offered only where there is something to retry, see `canRetry`.
const ACTIONS_BY_STATUS: Record<CampaignStatus, CampaignAction[]> = {
  draft: ['start'],
  running: ['pause', 'retry_unreached'],
  paused: ['resume', 'retry_unreached'],
  finished: ['retry_unreached'],
};

const canRetry = (campaign: OutboundCampaign) =>
  countOf(campaign.entry_counts, 'not_reached', 'failed') > 0;

type EntryFilters = { status?: EntryStatusFilter; query?: string };

function CampaignSummary({ campaign }: { campaign: OutboundCampaign }) {
  const { t } = useTranslation('outbound');
  const counts = campaign.entry_counts;

  return (
    <>
      <div className={styles.figures}>
        <KpiTile label={t('campaign.kpi.total')} value={formatNumber(counts.total)} />
        <KpiTile
          label={t('campaign.kpi.pending')}
          value={formatNumber(countOf(counts, 'pending', 'calling'))}
          note={
            counts.callback
              ? t('campaign.kpi.callbacks', { callbacks: formatNumber(counts.callback) })
              : undefined
          }
        />
        <KpiTile label={t('campaign.kpi.reached')} value={formatNumber(counts.reached)} />
        <KpiTile label={t('campaign.kpi.not_reached')} value={formatNumber(counts.not_reached)} />
        <KpiTile
          label={t('campaign.kpi.skipped')}
          value={formatNumber(countOf(counts, 'skipped', 'failed'))}
        />
      </div>

      <div className={styles.facts}>
        <FactTile label={t('campaign.settings.scenario')} value={campaign.scenario_name} />
        <FactTile label={t('campaign.settings.language')} value={languageName(campaign.language)} />
        <FactTile
          label={t('campaign.settings.window')}
          value={t('campaigns.window', {
            start: toHourMinute(campaign.window_start),
            end: toHourMinute(campaign.window_end),
            days: formatCallDays(campaign.call_days),
          })}
        />
        <FactTile
          label={t('campaign.settings.attempts')}
          value={t('campaign.settings.attemptsValue', {
            max: campaign.max_attempts,
            delay: campaign.retry_delay_minutes,
          })}
        />
        <FactTile
          label={t('campaign.settings.callbacks')}
          value={formatNumber(campaign.max_callbacks)}
        />
        <FactTile
          label={t('campaign.settings.pause')}
          value={t('campaign.settings.pauseValue', {
            seconds: campaign.pause_between_calls_seconds,
          })}
        />
        <FactTile
          className={styles.wide}
          label={t('campaign.settings.openingLine')}
          value={campaign.opening_line || t('campaign.settings.openingLineScenario')}
        />
        {campaign.created_by && (
          <FactTile label={t('campaign.settings.createdBy')} value={campaign.created_by} />
        )}
      </div>
    </>
  );
}

function CampaignEntries({ campaign }: { campaign: OutboundCampaign }) {
  const { t } = useTranslation(['outbound', 'common']);
  const isMobile = useScreenSize().width < 768;
  const [filters, setFilters] = useState<EntryFilters>({});
  // Pages loaded, as on the conversations list: "Pokaż więcej" asks for that many pages at once.
  const [loadedPages, setLoadedPages] = useState(1);
  const [leadId, setLeadId] = useState<number | undefined>();
  const [isExporting, setIsExporting] = useState(false);

  const changeFilters = (next: EntryFilters) => {
    setFilters({ status: next.status, query: next.query?.trim() || undefined });
    setLoadedPages(1);
  };
  const { filterValue, onQueryChange } = useDebouncedFilterQuery(filters, changeFilters);

  const rows = Math.min(OUTBOUND_PAGE_SIZE * loadedPages, OUTBOUND_MAX_ROWS);
  const { entries, total, isLoading, isFetching } = useCampaignEntries(
    campaign.id,
    { ...filters, page: 1, items_per_page: rows },
    campaign.status === 'running'
  );

  const handleExport = async () => {
    setIsExporting(true);
    try {
      downloadFile(await exportCampaignEntries(campaign.id, filters));
    } catch (error) {
      openNotification({
        type: 'error',
        message: outboundErrorMessage(error, t('outbound:campaign.exportFailed')),
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <SectionLabel>{t('outbound:entries.title')}</SectionLabel>

      <div className={styles.toolbar}>
        <div className={styles.chips}>
          <FilterChip
            label={t('common:states.all')}
            active={!filters.status}
            onClick={() => changeFilters({ ...filters, status: undefined })}
          />
          {ENTRY_STATUS_FILTERS.map((value) => (
            <FilterChip
              key={value}
              label={t(`outbound:entryStatus.${value}`)}
              active={filters.status === value}
              onClick={() => changeFilters({ ...filters, status: value })}
            />
          ))}
        </div>
        <div className={styles.actions}>
          <SearchField
            className={styles.search}
            value={filterValue}
            onChange={onQueryChange}
            placeholder={t('outbound:entries.searchPlaceholder')}
          />
          <ExportButton
            onPrepareExport={handleExport}
            isMobile={isMobile}
            isExporting={isExporting}
            isEmptyData={!isLoading && total === 0}
            label={t('outbound:campaign.actions.export')}
          />
        </div>
      </div>

      <CampaignEntriesTable
        entries={entries}
        maxAttempts={campaign.max_attempts}
        loading={isLoading}
        onOpenLead={setLeadId}
      />

      {entries.length < total && (
        <LoadMoreRows
          remaining={total - entries.length}
          atCeiling={rows >= OUTBOUND_MAX_ROWS}
          ceiling={OUTBOUND_MAX_ROWS}
          isLoading={isFetching}
          onClick={() => setLoadedPages((pages) => pages + 1)}
        />
      )}

      <LeadDrawer leadId={leadId} onClose={() => setLeadId(undefined)} />
    </>
  );
}

export function OutboundCampaignDetails() {
  const { t } = useTranslation(['outbound', 'common']);
  const { campaignId } = useParams();
  const id = Number(campaignId);
  const { company } = useOutboundContext();
  const { campaign, error } = useOutboundCampaign(id);
  const { scenarios } = useOutboundScenarios(company.id);
  const { runAction, pendingAction } = useCampaignAction();
  const [editOpen, setEditOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const backLink = <BackLink to={CAMPAIGNS_PATH} label={t('outbound:campaign.back')} />;

  if (!campaign && error) {
    return (
      <Flex vertical gap={14}>
        {backLink}
        {isNotFound(error) ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={outboundErrorMessage(error, t('outbound:errors.campaignNotFound'))}
          />
        ) : (
          <Alert
            type="error"
            showIcon
            message={outboundErrorMessage(error, t('outbound:errors.load'))}
          />
        )}
      </Flex>
    );
  }

  if (!campaign) return <Loader />;

  const actions = ACTIONS_BY_STATUS[campaign.status].filter(
    (action) => action !== 'retry_unreached' || canRetry(campaign)
  );

  const doneMessage = (action: CampaignAction, result: CampaignActionResult) => {
    if (action !== 'retry_unreached') return t(`outbound:campaign.actionDone.${action}`);
    if (!result.retried) return t('outbound:campaign.retriedNone');
    const retried = t('outbound:campaign.retried', { count: result.retried });
    return result.status === 'paused'
      ? `${retried} ${t('outbound:campaign.retriedPaused')}`
      : retried;
  };

  const handleAction = async (action: CampaignAction) => {
    try {
      const result = await runAction({ id, action });
      openNotification({ type: 'success', message: doneMessage(action, result) });
    } catch (actionError) {
      openNotification({
        type: 'error',
        message: outboundErrorMessage(actionError, t('outbound:actionFailed')),
      });
    }
  };

  return (
    <Flex vertical gap={14}>
      {backLink}

      <div className={styles.toolbar}>
        <div className={styles.heading}>
          <h2 className={styles.name}>{campaign.name}</h2>
          <StatusBadge
            variant={CAMPAIGN_STATUS_BADGE[campaign.status]}
            label={t(`outbound:campaignStatus.${campaign.status}`)}
          />
          {campaign.status === 'running' && (
            <span className={styles.secondary}>
              {t('outbound:refreshing', { seconds: OUTBOUND_POLL_INTERVAL_MS / 1000 })}
            </span>
          )}
        </div>

        <div className={styles.actions}>
          {actions.map((action, index) => (
            <Button
              key={action}
              buttonVariant={index === 0 ? 'primary' : 'secondary'}
              loading={pendingAction === action}
              disabled={!!pendingAction && pendingAction !== action}
              onClick={() => handleAction(action)}
            >
              {t(`outbound:campaign.actions.${action}`)}
            </Button>
          ))}
          <Button buttonVariant="secondary" onClick={() => setEditOpen(true)}>
            {t('outbound:campaign.actions.edit')}
          </Button>
          {campaign.status !== 'finished' && (
            <Button buttonVariant="secondary" onClick={() => setImportOpen(true)}>
              {t('outbound:campaign.actions.import')}
            </Button>
          )}
        </div>
      </div>

      <Callout>{campaignActivityLabel(campaign.activity)}</Callout>

      {!campaign.scenario_active && (
        <Alert
          type="warning"
          showIcon
          message={t('outbound:campaign.scenarioInactive', { name: campaign.scenario_name })}
        />
      )}

      {campaign.last_error && (
        <Alert
          type="warning"
          showIcon
          message={t('outbound:campaign.lastError')}
          description={campaignRefusalLabel(campaign.last_error)}
        />
      )}

      <CampaignSummary campaign={campaign} />

      <CampaignEntries campaign={campaign} />

      <CampaignFormModal
        open={editOpen}
        companyId={company.id}
        scenarios={scenarios}
        campaign={campaign}
        onClose={() => setEditOpen(false)}
        onSaved={() => {
          setEditOpen(false);
          openNotification({ type: 'success', message: t('outbound:saved') });
        }}
      />

      <CampaignImportModal open={importOpen} campaignId={id} onClose={() => setImportOpen(false)} />
    </Flex>
  );
}
